import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mt-4" }
const _hoisted_2 = { class: "flex items-center justify-center w-full" }
const _hoisted_3 = ["onDrop", "onDragover", "onDragenter"]
const _hoisted_4 = { class: "flex flex-col items-center justify-center pt-5 pb-6" }
const _hoisted_5 = { class: "border border-gray-200 p-2.5 rounded-lg mb-3" }
const _hoisted_6 = { class: "mb-2 text-sm text-gray-600 dark:text-gray-400" }
const _hoisted_7 = { class: "font-semibold text-blue-600" }
const _hoisted_8 = { class: "text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_9 = { class: "mt-4" }
const _hoisted_10 = {
  key: 0,
  class: "w-full"
}
const _hoisted_11 = { class: "flex items-center justify-between bg-rl-base-blue-50 px-3 py-2 rounded-lg" }
const _hoisted_12 = { class: "flex items-center gap-x-3" }
const _hoisted_13 = { class: "w-6 h-6 bg-rl-base-turquoise-400 rounded-full flex items-center justify-center" }
const _hoisted_14 = { class: "text-sm text-gray-800" }
const _hoisted_15 = {
  key: 1,
  class: "text-sm text-gray-600 text-center"
}
const _hoisted_16 = ["href"]
const _hoisted_17 = { class: "mx-auto flex items-center justify-center" }
const _hoisted_18 = { class: "mt-5 text-center" }
const _hoisted_19 = { class: "text-lg font-semibold mb-1" }
const _hoisted_20 = { class: "text-center" }
const _hoisted_21 = { class: "text-sm text-gray-600 mb-8" }

import * as XLSX from "xlsx";
import { ref, computed, PropType } from "vue";
import { CheckIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { School } from "@/interfaces/school.interface";
import { SchoolStructure } from "@/interfaces/school-structure.interface";
import { Subject } from "@/interfaces/subject.interface";
import i18n from "@/plugins/i18n";

interface DataRows {
  email?: string;
  lastName: string;
  firstName: string;
  studentNumber: number;
  password: string;
  [key: string]: string | number | undefined;
  id: string;
  lockedJourneys: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FileUploadWidgetComponent',
  props: {
  shouldGenerateEmail: {
    type: Boolean,
    required: true,
    default: false,
  },
  classGroupId: {
    type: String,
  },
  subjects: {
    type: Object as PropType<Subject[]>,
    required: true,
  },
  schoolData: {
    type: Object as PropType<School>,
    required: true,
  },
  schoolStructure: {
    type: Object as PropType<SchoolStructure>,
    required: true,
  },
  uploadType: {
    type: String,
  },
  grade: {
    type: Number,
  },
  validate: {
    type: Boolean,
    required: false,
    default: false,
  },
  requireGrade: {
    type: Boolean,
    required: false,
    default: true,
  },
  lockedJourneys: {
    type: Boolean,
    required: false,
    default: true,
  }, 
},
  emits: ["transmitLearnersToUpload", "closeUploadModal"],
  setup(__props, { emit }) {

const props = __props;

const showModal = ref(false);
const errorMessage = ref("");
const selectedFile = ref<File | null>(null);





const templateLink = computed(() => {
  if (props.uploadType === "archive_unarchive") {
    return "https://storage.googleapis.com/reflective-learning-dash-dev.appspot.com/templates/bulk_learner_archive_unarchive.xlsx";
  }

  const baseUrl = "https://storage.googleapis.com/reflective-learning-dash-dev.appspot.com/templates/";
  const type = props.uploadType === "grade" ? "grade" : "class";
  const emailPart = props.shouldGenerateEmail ? "_no_email" : "";
  
  return `${baseUrl}bulk_learner_${type}_upload${emailPart}.xlsx`;
});

const handleDroppedFile = (event: DragEvent) => {
  event.preventDefault();
  const file = event.dataTransfer?.files[0];
  if (file) selectedFile.value = file;
};

const handleSelectedFile = (event: Event) => {
  selectedFile.value = null;
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    selectedFile.value = file;
  }

  target.value = "";
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
};

const classColumnToSubjectName: { [key: string]: string } = {
  mathsclass: "maths",
  englishclass: "english",
};

// Function to find class ID by name
const findClassGroupByName = (classGroupName: string, subjectName: string, grade: number) => {
  if (!classGroupName) return null;

  const subjectId = props.subjects.find(
    (s) => s.name.toLowerCase() == subjectName
  )?.id;

  const trimmedClassGroupName = classGroupName.trim().toLowerCase();

  const allClassGroups = props.schoolStructure.classGroups;

  const classGroup = allClassGroups.find(
    (cls: any) =>
      cls.name?.trim().toLowerCase() === trimmedClassGroupName &&
      cls.gradeLevel === grade &&
      cls.subject === subjectId
  );

  if (classGroup) {
    return {
      classGroupId: classGroup._id,
      subjectId: classGroup.subject,
    };
  } else {
    return null;
  }
};

const processUploadedData = async (file: File) => {
  const reader = new FileReader();
  reader.onload = async (e: any) => {
    const data = e.target.result;
    const workbook = XLSX.read(data, { type: "binary" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const dataRows = XLSX.utils.sheet_to_json(worksheet) as DataRows[];

    // Normalize keys to lowercase
    const normalizedDataRows = dataRows.map((row: DataRows) => {
      const normalizedRow: any = {};
      for (const key in row) {
        normalizedRow[key.toLowerCase()] = row[key];
      }
      return normalizedRow;
    });

    let learners: any[] = [];

    if (props.shouldGenerateEmail) {
      learners = normalizedDataRows
        .map((row: any) => {
        
          const localLastName = row.lastname
            ? String(row.lastname).trim().replace(/\s/g, "")
            : ""; 

          const localFirstName = row.firstname
            ? String(row.firstname).trim().replace(/\s/g, "")
            : ""; 

          const mathsClassName = row.mathsclass ? String(row.mathsclass).trim() : "";
          const englishClassName = row.englishclass ? String(row.englishclass).trim() : "";

          if (props.validate) {
            const validateField = (
              condition: boolean,
              errorMessages: string,
              errorLog: string,
              row: any
            ) => {
              if (condition) {
                showModal.value = true;
                errorMessage.value = errorMessages;
                console.error(errorLog, row);
                throw new Error(errorMessages);
              }
            };
            
            // Validate grade
            validateField(
              !row.grade || isNaN(Number(row.grade.toString().trim())),
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.grade_invalid"
              ).toString(),
              "One or more learners have an invalid grade. Grade must be a valid number and cannot be empty in row:",
              row
            );

            // Validate first name and last name
            validateField(
              !localLastName.length || !localFirstName.length,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.name_surname"
              ).toString(),
              "First name or last name is missing in row:",
              row
            );

            // Validate Maths and English class names
            validateField(
              !mathsClassName && !englishClassName,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.required_class"
              ).toString(),
              "Maths class or English class is missing in row:",
              row
            );

            // Validate Password
            const password = row.password ? String(row.password).trim() : ""; 
            validateField(
              password.length < 6,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.password"
              ).toString(),
              "Password is missing in row:",
              row
            );
         }

          // Find class groups by name
          const mathsClassGroup = findClassGroupByName(
            mathsClassName,
            classColumnToSubjectName["mathsclass"],
            Number(row.grade.toString().trim())
          );
          const englishClassGroup = findClassGroupByName(
            englishClassName,
            classColumnToSubjectName["englishclass"],
            Number(row.grade.toString().trim())
          );

          // Build selectedClasses object
          const selectedClasses: { [subjectId: string]: string } = {};
          if (mathsClassGroup) {
            selectedClasses[mathsClassGroup.subjectId] =
              mathsClassGroup.classGroupId;
          }
          if (englishClassGroup) {
            selectedClasses[englishClassGroup.subjectId] =
              englishClassGroup.classGroupId;
          }

          return {
            email: `${localLastName.toLowerCase()}.${localFirstName.toLowerCase()}@${props.schoolData.domain}`.replace(/\s+/g, ""),
            lastName:String(row.lastname).trim(),
            firstName: String(row.firstname).trim(),
            classGroup: props.classGroupId,
            password: row.password ? String(row.password).trim() : "",
            studentNumber: row.studentnumber
              ? row.studentnumber.toString()
              : "",
            selectedClasses: selectedClasses,
            _id: row.id,
            grade: row.grade || null,
            lockedJourneys: row.lockedjourneys ? String(row.lockedjourneys).trim() : null,
            assessmentType: row.assessmenttype ? String(row.assessmenttype).trim() : null,
            newEmail: row.newemail ? row.newemail: null,
            preferredLanguage: row.locale ? row.locale: null,
          };
        })
        .filter((learner: any) => learner !== null);
    } else {
      learners = normalizedDataRows
        .map((row: any) => {
          
          const mathsClassName = row.mathsclass ? String(row.mathsclass).trim() : "";
          const englishClassName = row.englishclass ? String(row.englishclass).trim() : "";

          const localLastName = row.lastname
          ? String(row.lastname).trim().replace(/\s/g, "")
          : ""; 

          const localFirstName = row.firstname
            ? String(row.firstname).trim().replace(/\s/g, "")
            : ""; 

          if (props.validate) {  
            const validateFields = (
              condition: boolean,
              errorMessages: string,
              errorLog: string,
              row: any
            ) => {
              if (condition) {
                showModal.value = true;
                errorMessage.value = errorMessages;
                console.error(errorLog, row);
                throw new Error(errorMessages);
              }
            };

            // Validate grade
            validateFields(
              !row.grade || isNaN(Number(row.grade.toString().trim())),
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.grade_invalid"
              ).toString(),
              "One or more learners have an invalid grade. Grade must be a valid number and cannot be empty in row:",
              row
            );

            // Validate first name and last name
            validateFields(
              !localLastName.length || !localFirstName.length,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.name_surname"
              ).toString(),
              "First name or last name is missing in row:",
              row
            );

            // Validate Email
            validateFields(
              !row.email,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.email"
              ).toString(),
              "Email is missing in row:",
              row
            );

            // Validate Maths and English class names
            validateFields(
              !mathsClassName && !englishClassName,
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.required_class"
              ).toString(),
              "Maths class or English class is missing in row:",
              row
            );

            // Validate Password
            const password = row.password ? String(row.password).trim() : ""; 
            validateFields(
              password.length < 6,                                                                        
              i18n.global.t(
                "bulk_learner_upload_component.invalid_data_modal.password"
              ).toString(),
              "Password is missing in row:",
              row
            );
          }

          if (props.requireGrade) {
            // Find class groups by name
            const mathsClassGroup = findClassGroupByName(
              mathsClassName,
              classColumnToSubjectName["mathsclass"],
              Number(row.grade.toString().trim())
            );
            const englishClassGroup = findClassGroupByName(
              englishClassName,
              classColumnToSubjectName["englishclass"],
              Number(row.grade.toString().trim())
            );

            // Build selectedClasses object
            const selectedClasses: { [subjectId: string]: string } = {};
            if (mathsClassGroup) {
              selectedClasses[mathsClassGroup.subjectId] =
                mathsClassGroup.classGroupId;
            }
            if (englishClassGroup) {
              selectedClasses[englishClassGroup.subjectId] =
                englishClassGroup.classGroupId;
            } 

            return {
              email: row.email.trim().replace(/\s+/g, "").toLowerCase(),
              password: String(row.password).trim() ? String(row.password).trim(): "",
              lastName: String(row.lastname).trim() ? String(row.lastname).trim() : "",
              firstName: String(row.firstname).trim() ? String(row.firstname).trim() : "",
              classGroup: props.classGroupId,
              studentNumber: row.studentnumber
                ? row.studentnumber.toString()
                : "",
              selectedClasses: selectedClasses,
              _id: row.id,
              grade: row.grade || null,
              lockedJourneys: row.lockedjourneys ? String(row.lockedjourneys).trim() : null,
              assessmentType: row.assessmenttype ? String(row.assessmenttype).trim() : null,
              newEmail: row.newemail ? row.newemail.trim().replace(/\s+/g, "").toLowerCase() : null,
              preferredLanguage: row.locale ? row.locale : null,
            };
          } else {
            return {
              email: row.email.trim().replace(/\s+/g, "").toLowerCase(),
            }
          }
      })
      .filter((learner: any) => learner !== null);
    }

    if (props.lockedJourneys) {
      learners.forEach(learner => {
        if (learner.lockedJourneys === null) {
          delete learner.lockedJourneys;
        }
      });
    }

    emit("transmitLearnersToUpload", learners);
    emit("closeUploadModal", true);
  };
  reader.readAsBinaryString(file);
};

return (_ctx: any,_cache: any) => {
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _component_modal = _resolveComponent("modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("label", {
          for: "dropzone-file",
          onDrop: _withModifiers(handleDroppedFile, ["prevent"]),
          onDragover: _withModifiers(handleDragOver, ["prevent"]),
          onDragenter: _withModifiers(handleDragEnter, ["prevent"]),
          class: "flex flex-col items-center justify-center w-full border-2 border-blue-600 rounded-xl cursor-pointer bg-white hover:bg-base-blue-100"
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_rl_icon, {
                icon: 'rl-file-upload',
                title: 'rl file upload',
                "aria-hidden": "true"
              })
            ]),
            _createElementVNode("p", _hoisted_6, [
              _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("file_upload_component.click_upload")), 1),
              _createTextVNode(" " + _toDisplayString(_ctx.$t("file_upload_component.drag_drop")), 1)
            ]),
            _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t("file_upload_component.accepted_files")), 1)
          ]),
          _createElementVNode("input", {
            type: "file",
            class: "hidden",
            id: "dropzone-file",
            onChange: handleSelectedFile,
            accept: ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet/svg+xml,.csv,image/jpeg,image/gif"
          }, null, 32)
        ], 40, _hoisted_3)
      ]),
      _createElementVNode("div", _hoisted_9, [
        (selectedFile.value !== null)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("span", _hoisted_13, [
                    _createVNode(_unref(CheckIcon), { class: "w-4 h-4 text-white" })
                  ]),
                  _createElementVNode("p", _hoisted_14, _toDisplayString(selectedFile.value.name), 1)
                ]),
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (selectedFile.value = null))
                }, [
                  _createVNode(_unref(XMarkIcon), { class: "w-5 h-5 text-red-500" })
                ])
              ]),
              _createElementVNode("button", {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (processUploadedData(selectedFile.value))),
                type: "button",
                class: "w-full rounded-lg bg-blue-600 mt-4 py-2.5 font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rl-base-blue-400"
              }, _toDisplayString(_ctx.$t("file_upload_component.preview")), 1)
            ]))
          : (_openBlock(), _createElementBlock("p", _hoisted_15, [
              _createTextVNode(_toDisplayString(_ctx.$t("file_upload_component.help_text")) + " ", 1),
              _createElementVNode("a", {
                href: templateLink.value,
                class: "text-blue-600 font-semibold"
              }, _toDisplayString(_ctx.$t("file_upload_component.download_template")), 9, _hoisted_16)
            ]))
      ])
    ]),
    _createVNode(_component_modal, {
      showCloseButton: true,
      widthClass: 'max-w-lg px-4 py-6 rounded-lg',
      open: showModal.value,
      onLocalModalOpen: _cache[2] || (_cache[2] = ($event: any) => (showModal.value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_17, [
            _createVNode(_component_rl_icon, {
              icon: 'rl-red-fail',
              title: 'rl red fail',
              "aria-hidden": "true"
            })
          ]),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("h3", _hoisted_19, _toDisplayString(_ctx.$t('bulk_learner_upload_component.invalid_data_modal.heading')), 1)
          ]),
          _createElementVNode("div", _hoisted_20, [
            _createElementVNode("p", _hoisted_21, _toDisplayString(errorMessage.value), 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})